import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Copy, Check, Printer, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Zoopla-Ready Summary — presents an existing Keystone property in the exact
 * field order the Zoopla Pro "Add Property" screen asks for, with one-click
 * copy on every field so double-entry drops from ~15 min to ~3 min.
 *
 * Zero integration with Zoopla itself — this is a copy-paste helper, so no
 * API keys, no data agreements, no risk of breakage when Zoopla changes.
 */

const formatPrice = (p) => (p ? `£${p.toLocaleString("en-GB")} pcm` : "");

// Split a UK address into a first-line and a postcode fragment.
// Zoopla asks for postcode separately from the rest of the address.
const splitPostcode = (address) => {
  if (!address) return { line1: "", postcode: "" };
  // Standard UK postcode pattern (case-insensitive, spaced or unspaced)
  const re = /,?\s*([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i;
  const m = address.match(re);
  if (!m) return { line1: address.trim(), postcode: "" };
  return {
    line1: address.slice(0, m.index).replace(/,\s*$/, "").trim(),
    postcode: m[1].toUpperCase().replace(/\s+/g, " "),
  };
};

const CopyField = ({ label, value, multiline = false, testid, hint }) => {
  const [copied, setCopied] = useState(false);
  const empty = !value || String(value).trim() === "";

  const handleCopy = async () => {
    if (empty) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`Copied — ${label}`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — please copy manually.");
    }
  };

  return (
    <div className="grid grid-cols-[130px_1fr_auto] items-start gap-3 py-2.5 border-b border-slate-100 last:border-b-0">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 pt-1">
        {label}
      </div>
      <div
        className={`text-sm text-navy break-words ${empty ? "text-slate-400 italic" : ""} ${multiline ? "whitespace-pre-wrap" : ""}`}
        data-testid={testid ? `${testid}-value` : undefined}
      >
        {empty ? "— not set" : value}
        {hint && !empty && (
          <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        disabled={empty}
        className="h-7 px-2 text-xs"
        data-testid={testid}
      >
        {copied ? (
          <>
            <Check className="w-3.5 h-3.5 mr-1 text-green-600" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-3.5 h-3.5 mr-1" />
            Copy
          </>
        )}
      </Button>
    </div>
  );
};

export function ZooplaSummaryDialog({ open, onOpenChange, property }) {
  const summary = useMemo(() => {
    if (!property) return null;
    const { line1, postcode } = splitPostcode(property.address);
    const featuresList = Array.isArray(property.features)
      ? property.features.filter(Boolean)
      : [];
    const imageUrls = Array.isArray(property.images)
      ? property.images.filter(Boolean)
      : [];

    return {
      title: property.title || "",
      addressLine1: line1,
      postcode,
      location: property.location || "",
      propertyType: property.property_type || "",
      bedrooms: property.bedrooms ?? "",
      bathrooms: property.bathrooms ?? "",
      pricePcm: formatPrice(property.price),
      priceRaw: property.price ? String(property.price) : "",
      available: property.available ? "Available" : "Let Agreed",
      description: property.description || "",
      features: featuresList.join("\n"),
      featuresCount: featuresList.length,
      images: imageUrls.join("\n"),
      imagesCount: imageUrls.length,
    };
  }, [property]);

  if (!property || !summary) return null;

  const fullSummary = [
    `KEYSTONE PROPERTIES — Zoopla-Ready Summary`,
    `Generated: ${new Date().toLocaleString("en-GB")}`,
    "",
    `Title: ${summary.title}`,
    `Address (line 1): ${summary.addressLine1}`,
    `Postcode: ${summary.postcode}`,
    `Town / Area: ${summary.location}`,
    `Property Type: ${summary.propertyType}`,
    `Bedrooms: ${summary.bedrooms}`,
    `Bathrooms: ${summary.bathrooms}`,
    `Rent (per calendar month): ${summary.pricePcm}`,
    `Availability: ${summary.available}`,
    "",
    `--- DESCRIPTION ---`,
    summary.description,
    "",
    `--- KEY FEATURES (${summary.featuresCount}) ---`,
    summary.features || "(none set)",
    "",
    `--- IMAGES (${summary.imagesCount}) ---`,
    summary.images || "(none set)",
  ].join("\n");

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(fullSummary);
      toast.success("Full summary copied — paste into your notes.");
    } catch {
      toast.error("Could not copy — please copy manually.");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print this summary.");
      return;
    }
    const escapeHtml = (s) =>
      String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const bulletList = (str) =>
      str
        ? `<ul>${String(str)
            .split("\n")
            .map((line) => (line.trim() ? `<li>${escapeHtml(line.trim())}</li>` : ""))
            .join("")}</ul>`
        : "<em>(none)</em>";
    printWindow.document.write(`
      <!doctype html><html><head><meta charset="utf-8">
      <title>${escapeHtml(summary.title)} — Zoopla-Ready Summary</title>
      <style>
        body { font-family: -apple-system, Arial, sans-serif; color: #1a2744; max-width: 780px; margin: 32px auto; padding: 0 24px; }
        h1 { font-size: 22px; margin: 0 0 4px; }
        .eyebrow { color: #C5A059; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; vertical-align: top; font-size: 14px; }
        td.label { color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; width: 32%; }
        h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #C5A059; margin-top: 28px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
        p, ul, li { font-size: 14px; line-height: 1.6; }
        ul { padding-left: 22px; }
        .foot { color: #64748b; font-size: 11px; margin-top: 40px; }
        @media print { body { margin: 0; } }
      </style></head><body>
      <div class="eyebrow">Keystone Properties · Zoopla-Ready Summary</div>
      <h1>${escapeHtml(summary.title)}</h1>
      <table>
        <tr><td class="label">Address (line 1)</td><td>${escapeHtml(summary.addressLine1)}</td></tr>
        <tr><td class="label">Postcode</td><td><strong>${escapeHtml(summary.postcode)}</strong></td></tr>
        <tr><td class="label">Town / Area</td><td>${escapeHtml(summary.location)}</td></tr>
        <tr><td class="label">Property Type</td><td>${escapeHtml(summary.propertyType)}</td></tr>
        <tr><td class="label">Bedrooms</td><td>${escapeHtml(summary.bedrooms)}</td></tr>
        <tr><td class="label">Bathrooms</td><td>${escapeHtml(summary.bathrooms)}</td></tr>
        <tr><td class="label">Rent (PCM)</td><td><strong>${escapeHtml(summary.pricePcm)}</strong></td></tr>
        <tr><td class="label">Availability</td><td>${escapeHtml(summary.available)}</td></tr>
      </table>
      <h2>Description</h2>
      <p>${escapeHtml(summary.description).replace(/\n/g, "<br>")}</p>
      <h2>Key Features (${summary.featuresCount})</h2>
      ${bulletList(summary.features)}
      <h2>Images (${summary.imagesCount})</h2>
      ${bulletList(summary.images)}
      <div class="foot">Generated ${new Date().toLocaleString("en-GB")} · thekeystoneproperties.co.uk</div>
      <script>window.onload = () => window.print();</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="zoopla-summary-dialog"
      >
        <div className="bg-navy text-white px-6 py-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-gold mb-1">
            Zoopla-Ready Summary
          </div>
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl text-white font-heading">
              {summary.title}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm mt-1">
              Copy each field into your Zoopla Pro listing. All values match your Keystone admin — no double-entry mistakes.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5">
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            <CopyField label="Title" value={summary.title} testid="zs-copy-title" />
            <CopyField label="Address (line 1)" value={summary.addressLine1} testid="zs-copy-address1" hint="Postcode is a separate field on Zoopla" />
            <CopyField label="Postcode" value={summary.postcode} testid="zs-copy-postcode" />
            <CopyField label="Town / Area" value={summary.location} testid="zs-copy-location" />
            <CopyField label="Property Type" value={summary.propertyType} testid="zs-copy-type" />
            <CopyField label="Bedrooms" value={summary.bedrooms} testid="zs-copy-bedrooms" />
            <CopyField label="Bathrooms" value={summary.bathrooms} testid="zs-copy-bathrooms" />
            <CopyField label="Rent (PCM)" value={summary.pricePcm} testid="zs-copy-price" hint={`Numeric only: ${summary.priceRaw}`} />
            <CopyField label="Availability" value={summary.available} testid="zs-copy-availability" />
            <CopyField
              label="Description"
              value={summary.description}
              multiline
              testid="zs-copy-description"
              hint={`${summary.description.length} characters`}
            />
            <CopyField
              label={`Key Features · ${summary.featuresCount}`}
              value={summary.features}
              multiline
              testid="zs-copy-features"
              hint="Paste as newline-separated bullets on Zoopla"
            />
            <CopyField
              label={`Images · ${summary.imagesCount}`}
              value={summary.images}
              multiline
              testid="zs-copy-images"
              hint="Download each URL, then re-upload to Zoopla"
            />
          </div>

          <p className="text-xs text-slate-500 mt-5 leading-relaxed">
            <strong className="text-navy">Tip:</strong> open Zoopla Pro side-by-side, then work top-to-bottom clicking <span className="text-navy font-medium">Copy</span> on each row and pasting straight into the matching Zoopla field. Images need to be re-uploaded to Zoopla directly (they don't accept remote URLs).
          </p>
        </div>

        <DialogFooter className="px-6 py-4 bg-slate-50 border-t border-slate-200 gap-2 sm:justify-between">
          <div className="text-xs text-slate-500 self-center">
            All values pulled from Keystone admin · read-only
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrint}
              data-testid="zs-print"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCopyAll}
              data-testid="zs-copy-all"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy full summary
            </Button>
            <Button
              type="button"
              asChild
              className="bg-gold hover:bg-gold-light text-navy font-semibold"
            >
              <a
                href="https://pro.zoopla.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="zs-open-zoopla"
              >
                Open Zoopla Pro
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ZooplaSummaryDialog;
